import React from 'react';
import { useTranslation } from 'react-i18next';
import { faArrowRightArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import WarningModal from 'components/modals/WarningModal';
import UpgradeSeatsForm from '../../views/settings/billing/UpgradeSeatsForm';
import { saveData } from 'services/api/saveData';
import { useGlobalContext } from 'GlobalContext';
import { useSubcontractorDayPassCheck } from './useSubcontractorDayPassCheck';

/*
 * useSubcontractorConversion.tsx
 * Hook which converts a user with a field only role into a
 * subcontractor and a subcontractor back into a user. Both
 * directions ask for a confirmation first. Converting into a user
 * needs a free seat, so the manage seats modal is shown when the
 * backend reports that no seat is available and the conversion is
 * retried after the seats are added. Converting into a subcontractor
 * needs day passes, which reuses the day pass warning modal
 */

// The error code the backend returns when no seat is available
export const NO_SEATS_ERROR_CODE = 'no_seats_available';

export const useSubcontractorConversion = () => {
    const { t } = useTranslation();
    const { initializeModal, closeModal, modalStack } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const { handleDayPassError } = useSubcontractorDayPassCheck();
    const topModal = modalStack[modalStack.length - 1];

    // Close the form modal the conversion started from and refetch its list
    const finishConversion = (message: string) => {
        setFloatingAlert({ type: 'success', message });
        closeModal();
        if (topModal?.props?.onSuccess) topModal.props.onSuccess();
    };

    // Show the general error of a failed conversion as alert
    const showConversionError = (error: any) => {
        const general = error?.response?.data?.general;
        setFloatingAlert({ type: 'danger', message: general || 'subcontractor.conversion.failed_message' });
    };

    // Convert a user into a subcontractor after the confirmation
    const convertUserToSubcontractor = (userId: number) => {
        initializeModal(
            <WarningModal
                type='info'
                icon={faArrowRightArrowLeft}
                header='subcontractor.conversion.to_subcontractor_header'
                message='subcontractor.conversion.to_subcontractor_message'
                buttonLabel='subcontractor.conversion.to_subcontractor_button'
                onApproval={async () => {
                    try {
                        await saveData({ apiUrl: `convert_user_to_subcontractor/${userId}`, method: 'post', data: {} });
                        finishConversion('subcontractor.conversion.to_subcontractor_success');
                    } catch (error) {
                        if (handleDayPassError(error)) return;
                        showConversionError(error);
                    }
                }}
            />,
            { modalSize: 'extra-small' }
        );
    };

    // Post the subcontractor conversion, showing the seats modal when no seat is free
    const postSubcontractorToUser = async (userId: number) => {
        try {
            await saveData({ apiUrl: `convert_subcontractor_to_user/${userId}`, method: 'post', data: {} });
            finishConversion('subcontractor.conversion.to_user_success');
        } catch (error: any) {
            if (error?.response?.data?.error_code === NO_SEATS_ERROR_CODE) {
                openManageSeatsModal(userId);
                return;
            }
            showConversionError(error);
        }
    };

    // Open the manage seats modal and retry the conversion after the seats are added
    const openManageSeatsModal = (userId: number) => {
        initializeModal(
            <UpgradeSeatsForm />, {
                title: t('billing.general.manage_seats_header'),
                modalSize: 'small-medium',
                onSuccess: () => postSubcontractorToUser(userId)
            }
        );
    };

    // Convert a subcontractor into a user after the confirmation
    const convertSubcontractorToUser = (userId: number) => {
        initializeModal(
            <WarningModal
                type='info'
                icon={faArrowRightArrowLeft}
                header='subcontractor.conversion.to_user_header'
                message='subcontractor.conversion.to_user_message'
                buttonLabel='subcontractor.conversion.to_user_button'
                onApproval={() => postSubcontractorToUser(userId)}
            />,
            { modalSize: 'extra-small' }
        );
    };

    return { convertUserToSubcontractor, convertSubcontractorToUser };
};
